import { g } from "analytics";
import { commandProcessor } from "command/command";
import { Align } from "editing/arrange/align";
import { Distribute } from "editing/arrange/distribute";
import { getDescendantIDs } from "editing/grouping/operations";
import { createSingleLayerLiquidPropsSections } from "panel/liquid_panel_ui";
import { createOrderingButtons } from "panel/ordering_ui";
export function sectionsForMultipleItems(selected, app) {
    let elements = [];
    if ((selected.standaloneObjectsCount + selected.groupsCount) > 1) {
        elements.push(...createAlignAndDistributeIcons(selected, app));
    }
    // Reordering should happen on items only, on the uncategorised items
    // so that items in groups, can be moved to.
    if ((selected.standaloneObjectsCount + selected.groupsCount) > 0) {
        elements.push(createOrderingButtons(selected.items.map(i => i.id)));
    }
    if (selected.shouldAllowGrouping || selected.shouldAllowUngrouping) {
        if (selected.shouldAllowGrouping) {
            elements.push({
                spec: "button",
                key: "groupObjects",
                label: "Grouping",
                buttonLabel: "Group objects",
                icon: "object-group",
                onClick: () => {
                    const command = selected.createGroupObjectsCommand();
                    if (command) {
                        g("Grouping", "Group");
                        commandProcessor.execute(command);
                    }
                }
            });
        }
        if (selected.shouldAllowUngrouping) {
            elements.push({
                spec: "button",
                key: "ungroupObjects",
                label: "Grouping",
                buttonLabel: "Ungroup objects",
                icon: "object-ungroup",
                onClick: () => {
                    const command = selected.createUngroupObjectsCommand();
                    if (command) {
                        g("Grouping", "Ungroup");
                        commandProcessor.execute(command);
                    }
                }
            });
        }
    }
    return {
        title: () => selected.displayString,
        icon: "mouse-pointer",
        tabs: [
            {
                type: "tools",
                sections: [
                    {
                        header: "Tools",
                        icon: "wrench",
                        hideHeaderIfFirst: true,
                        elements,
                    },
                ],
            },
            ...(selected.isItemsWithLiquidOnly ? [
                {
                    type: "liquid",
                    colorDot: selected.getConsistentItemsLiquidColor(),
                    sections: createSingleLayerLiquidPropsSections(selected.items, app, selected.isItemsSupportingMeniscus)
                }
            ] : [])
        ]
    };
}
function createAlignAndDistributeIcons(selected, app) {
    // Align is only supported for items and arrows and groups.
    // If an arrow is inside a group, the arrow will be ignored for purposes of alignment.
    const itemComponents = selected.standaloneItems.map(i => app.getObjectComponent(i.id));
    const arrowComponents = selected.standaloneArrows.map(a => app.getObjectComponent(a.id));
    const groupAlignables = selected.rootGroups.map(g => new AlignableGroup(g, app));
    const alignables = [...itemComponents, ...arrowComponents, ...groupAlignables];
    return [{
            spec: "horizontal-icon-buttons",
            label: "Align",
            key: "HorizontalAlign",
            buttons: [
                {
                    buttonLabel: "Align left edges", icon: "k,align-h-left",
                    onClick: () => {
                        const command = Align.align(alignables, "left");
                        if (command) {
                            g("AlignItems", "left");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Align horizontal center", icon: "k,align-h-center",
                    onClick: () => {
                        const command = Align.alignCenters(alignables, "x");
                        if (command) {
                            g("AlignItems", "horizontal-centers");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Align right edges", icon: "k,align-h-right",
                    onClick: () => {
                        const command = Align.align(alignables, "right");
                        if (command) {
                            g("AlignItems", "right");
                            commandProcessor.execute(command);
                        }
                    }
                }
            ]
        }, {
            spec: "horizontal-icon-buttons",
            label: " ",
            key: "VerticalAlign",
            buttons: [
                {
                    buttonLabel: "Align top edges", icon: "k,align-v-top",
                    onClick: () => {
                        const command = Align.align(alignables, "top");
                        if (command) {
                            g("AlignItems", "top");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Align vertical centers", icon: "k,align-v-center",
                    onClick: () => {
                        const command = Align.alignCenters(alignables, "y");
                        if (command) {
                            g("AlignItems", "vertical-centers");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Align bottom edges", icon: "k,align-v-bottom",
                    onClick: () => {
                        const command = Align.align(alignables, "bottom");
                        if (command) {
                            g("AlignItems", "bottom");
                            commandProcessor.execute(command);
                        }
                    }
                }
            ]
        }, {
            spec: "horizontal-icon-buttons",
            label: "Distribute",
            key: "Distribute",
            buttons: [
                {
                    buttonLabel: "Distribute centers horizontally", icon: "k,distribute-h-center",
                    onClick: () => {
                        const command = Distribute.distributeCenters(alignables, "x");
                        if (command) {
                            g("AlignItems", "distribute-horizontal-centers");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Distribute horizontal gaps", icon: "k,distribute-h-gap",
                    onClick: () => {
                        const command = Distribute.distributeGaps(alignables, "x");
                        if (command) {
                            g("AlignItems", "distribute-horizontal-gaps");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Distribute centers vertically", icon: "k,distribute-v-center",
                    onClick: () => {
                        const command = Distribute.distributeCenters(alignables, "y");
                        if (command) {
                            g("AlignItems", "distribute-vertical-centers");
                            commandProcessor.execute(command);
                        }
                    }
                },
                {
                    buttonLabel: "Distribute vertical gaps", icon: "k,distribute-v-gap",
                    onClick: () => {
                        const command = Distribute.distributeGaps(alignables, "y");
                        if (command) {
                            g("AlignItems", "distribute-vertical-gaps");
                            commandProcessor.execute(command);
                        }
                    }
                },
            ]
        }];
}
/** Provides Alignable implementation for a group. */
class AlignableGroup {
    constructor(group, app) {
        this.group = group;
        this.app = app;
    }
    get alignmentBounds() {
        // Retrieve objects. Only select apparatus items.
        const childIds = getDescendantIDs(this.group).filter(i => i.type === "apparatus");
        const allBounds = childIds.map(id => this.app.getObjectComponent(id)).map(c => c.bounds);
        // Merge all bounds.
        const bounds = allBounds[0];
        for (const bound of allBounds) {
            bounds.united(bound);
        }
        return bounds;
    }
    get id() {
        return this.group.id;
    }
}
